// src/pages/Tracker.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Navbar from '../components/navbar';
import MetricsCards from './MetricsCards';
import TraceabilityTimeline from './TraceabilityTimeline';

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

const TRANSACTIONAL_KEYS = ['dpp_created_by', 'data_carrier_type', 'link_to_digital_copy', 'ownership', 'passport_availability_period'];

function getUserRole() {
  try {
    const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
    if (!token) return 'Manufacturer';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.type || payload.role || 'Manufacturer';
  } catch { return 'Manufacturer'; }
}

function Tracker() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [passport, setPassport]               = useState(null);
  const [passportLoading, setPassportLoading] = useState(true);
  const [visibleFields, setVisibleFields]     = useState(null); // null = show all

  const userRole = getUserRole();

  useEffect(() => {
    if (!id) return;
    const fetchPassport = async () => {
      try {
        setPassportLoading(true);
        const token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(
          `${API_BASE_URL}/getproduct/${id}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        setPassport(data);
      } catch (err) {
        console.error('Passport fetch error:', err);
      } finally {
        setPassportLoading(false);
      }
    };
    fetchPassport();
  }, [id]);

  // Fetch transactional field visibility for non-Manufacturer roles
  useEffect(() => {
    if (userRole === 'Manufacturer') return;
    const fetchRoleAccess = async () => {
      try {
        const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/role-access`, {
          headers: { 'Content-Type': 'application/json', 'X-User-Role': userRole, Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.data[userRole]) {
          const roleData = data.data[userRole];
          setVisibleFields(TRANSACTIONAL_KEYS.filter(k => roleData[k]?.isVisible));
        } else {
          setVisibleFields([]);
        }
      } catch (err) {
        console.error('Role access fetch error:', err);
        setVisibleFields(null); // fallback: show all
      }
    };
    fetchRoleAccess();
  }, [userRole]);

  return (
    <div className="min-h-screen bg-[#EDEDED]">
      <Navbar />

      {/* Top bar */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl shadow">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
          Back
        </button>
        <h2 className="text-blue-600 font-medium text-sm sm:text-base">Product Journey</h2>
      </div>

      <div className="px-4 sm:px-8 lg:px-12 pt-6 pb-8">
        <div className="flex gap-6 items-start">

          {/* LEFT */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">

            {/* ── Card 1: Supply Chain Journey — timeline only ── */}
            {/* <div className="bg-white rounded-2xl shadow p-8">
              <h3 className="text-base font-semibold text-gray-800 mb-6">Supply Chain Journey</h3>
              <TraceabilityTimeline dppId={id} />
            </div> */}

            {/* ── Card 2: Passport details cards ── */}
            <div className="bg-white rounded-2xl shadow p-8">
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 20px' }}>
                Traceability Events &amp; Passport Details
              </h3>
              {passportLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                  Loading details...
                </div>
              ) : passport ? (
                <TraceabilityTimeline
                  dppId={id}
                  transactionalData={passport}
                  visibleFields={visibleFields}
                />
              ) : (
                <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>No passport details found.</p>
              )}
            </div>

          </div>

          {/* RIGHT: sidebar */}
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <MetricsCards vertical={true} />
          </div>

        </div>
      </div>
    </div>
  );
}

export default Tracker;